import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  purchasingAPI,
  ForecastDateBucket,
  ForecastFilterOptions,
} from '../../../lib/api';
import { useIsCustomer, useIsSupplier } from '../../../hooks/usePortalMode';
import { useAuth } from '../../../contexts/AuthContext';
import { useMenuSettings } from '../../../contexts/MenuSettingsContext';
import { useView } from '../../../contexts/ViewContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Card, CardContent, CardHeader } from '../../ui/card';
import { Pagination } from '../../ui/pagination';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ForecastCascadeFilterOptions } from './forecast/useForecastFilterCascades';
import { ForecastFiltersPanel } from './forecast/ForecastFiltersPanel';
import { ForecastToolbar } from './forecast/ForecastToolbar';
import { ForecastGridControls } from './forecast/ForecastGridControls';
import { ForecastGridTable } from './forecast/ForecastGridTable';
import { ForecastLegend } from './forecast/ForecastLegend';
import ForecastDrilldownDialog from './forecast/ForecastDrilldownDialog';
import { useForecastSnapshotRefresh } from './forecast/useForecastSnapshotRefresh';
import { useForecastExport } from './forecast/useForecastExport';
import { buildForecastTableParams } from './forecast/forecastQueryParams';
import {
  EMPTY_FORECAST_FILTERS,
  ForecastFilters,
  ForecastGridLayout,
  ForecastGridOptions,
  ForecastTableRowWithStock,
  RECORD_TYPE_LABELS,
  RECORD_TYPE_ORDER,
  rowField,
  rowId,
} from './forecast/forecastGridTypes';

const PAGE_SIZE = 50;

export default function ForecastTab() {
  const isCustomer = useIsCustomer();
  const isSupplier = useIsSupplier();
  const { isFeatureEnabled } = useMenuSettings();
  const { formatQuantity } = useFormatters();
  // CR-5286: forward dashboard top-bar selection (view_level + group/
  // company/site) so the Forecasting tab scopes to the same entity the
  // user is viewing instead of returning every tenant's data.
  const { viewLevel, getViewQueryParams, viewKey } = useView();
  const showStockStatus = isFeatureEnabled('supplier_stock_levels');
  // Firm-window column belongs to the Part Schedule module — only shown for
  // tenants that have schedule lines turned on (scheduling tenants). On other tenants it
  // would just render '-' across every row.
  const showFirmDays = isFeatureEnabled('po_line_schedules');
  // Admin Setup → Forecast → "Weekly horizon only": hide the monthly buckets
  // (and Future) so tenants planning on the 25-week window don't scroll a
  // two-year grid.
  const weeksOnly = isFeatureEnabled('forecast_weeks_only');
  // Supplier-part filter only for schedule-line (schedule-line-handler) tenants.
  const { user } = useAuth();
  const isScheduleTenant = user?.import_handler === 'dennis_eagle';

  const [data, setData] = useState<ForecastTableRowWithStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [partsCount, setPartsCount] = useState(0);
  // Admin Setup → Forecast column switches: decided server-side on the
  // customer whose demand is on screen, so a supplier sees the customer's
  // choice too (Customer review 11 Sep, item 35).
  const [gridOptions, setGridOptions] = useState<ForecastGridOptions>({});
  // Quick part-number search lives outside the Filters drawer so users
  // don't have to expand it for the most common lookup.
  const [quickSearch, setQuickSearch] = useState('');
  const quickSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [buckets, setBuckets] = useState<{
    weeks: ForecastDateBucket[];
    months: ForecastDateBucket[];
  }>({ weeks: [], months: [] });
  const [filterOptions, setFilterOptions] = useState<ForecastFilterOptions | null>(null);
  // CR-6440: Cascade filter options for dependent filtering
  const [cascadeFilterOptions, setCascadeFilterOptions] = useState<ForecastCascadeFilterOptions | null>(null);
  // Drill-down modal state
  const [drilldownOpen, setDrilldownOpen] = useState(false);
  const [drilldownData, setDrilldownData] = useState<Record<string, unknown>[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState('');

  const tableRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  // Monotonic id of the newest table fetch — see fetchData's race guard.
  const fetchRequestRef = useRef(0);

  // Draft filters (the drawer) and the filters the grid is showing.
  const [filters, setFilters] = useState<ForecastFilters>(EMPTY_FORECAST_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ForecastFilters>(EMPTY_FORECAST_FILTERS);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Clean up the quick-search debounce timer on unmount.
  useEffect(() => {
    return () => {
      if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
    };
  }, []);

  const loadFilterOptions = async () => {
    try {
      // Fetch both forecast filter options and line filter options in parallel
      const [forecastRes, lineRes] = await Promise.all([
        purchasingAPI.getForecastFilterOptions(),
        purchasingAPI.getLineFilterOptions(),
      ]);
      setFilterOptions(forecastRes.data);

      // CR-6440: Build cascade filter options from line filter data
      const lineData = lineRes.data;

      // Map commodity_group ID to group name for cascade mapping
      const commodityGroupIdToName: Record<number, string> = {};
      (lineData.commodity_groups || []).forEach((g: { id: number; name: string }) => {
        commodityGroupIdToName[g.id] = g.name;
      });

      setCascadeFilterOptions({
        suppliers: lineData.suppliers || [],
        commodityGroups: forecastRes.data.commodity_groups || [],
        // Map commodity types to their group NAMES (matching forecast filter values)
        commodityTypes: (lineData.commodity_types || []).map((ct: { id: number; name: string; commodity_group_id: number }) => ({
          name: ct.name,
          commodity_group: commodityGroupIdToName[ct.commodity_group_id] || '',
        })),
        supplierGroups: (forecastRes.data.supplier_groups || []).map((g: { id: number; name: string }) => ({
          id: g.id,
          name: g.name,
        })),
        sites: (lineData.customers || []).map((c: { id: number; name: string; group?: number | null; country?: string | null }) => ({
          id: c.id,
          name: c.name,
          group: c.group,
          country: c.country,
        })),
        divisions: (lineData.divisions || []).map((d: { id: number; name: string }) => ({
          id: d.id,
          name: d.name,
        })),
        siteCountries: lineData.site_countries || [],
        supplierCountries: lineData.supplier_countries || [],
      });
    } catch (err: unknown) {
      console.error('Error loading filter options:', err);
    }
  };

  const fetchData = async () => {
    // Race guard: progressive typing in the quick box fires a request per
    // debounce tick, and a broader earlier query can return AFTER the
    // narrower newest one (it matches more rows) — without this its stale
    // rows would overwrite the correct result.
    const requestId = ++fetchRequestRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getForecastTableData(buildForecastTableParams({
        page: currentPage,
        pageSize: PAGE_SIZE,
        isSupplier,
        viewLevel,
        viewParams: getViewQueryParams(),
        appliedFilters,
        showStockStatus,
      }));
      if (requestId !== fetchRequestRef.current) return; // superseded
      // CR-4727: Handle both 'results' and 'rows' keys from backend
      setData(response.data.results ?? response.data.rows ?? []);
      const rowCount = response.data.total_count ?? response.data.total ?? 0;
      setTotalCount(rowCount);
      setPartsCount(response.data.parts_count ?? rowCount);
      setTotalPages(Math.ceil(rowCount / PAGE_SIZE));
      setBuckets(response.data.buckets || { weeks: [], months: [] });
      setGridOptions({
        hide_supplier_part_no: response.data.hide_supplier_part_no,
        show_po_number: response.data.show_po_number,
        show_total_row: response.data.show_total_row,
        show_order_requirements: response.data.show_order_requirements,
      });
    } catch (err: unknown) {
      if (requestId !== fetchRequestRef.current) return;
      console.error('Error fetching forecast data:', err);
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError.response?.data?.detail || 'Failed to load forecast data');
    } finally {
      if (requestId === fetchRequestRef.current) setLoading(false);
    }
  };

  // Fetch when page, filters, stock status feature, portal side or top-bar
  // view selection (CR-5286) changes. fetchData itself is intentionally
  // omitted from deps — we trigger explicitly off state changes.
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters, showStockStatus, isSupplier, viewKey]);

  // Latest fetchData for the snapshot poller, which outlives this render.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;
  const reloadAfterRefresh = useCallback(() => fetchDataRef.current(), []);
  const { refreshStatus, refreshing, handleRefreshSnapshot } = useForecastSnapshotRefresh(reloadAfterRefresh);
  const { exporting, handleExport } = useForecastExport(appliedFilters, viewLevel, getViewQueryParams);

  const handleSearch = () => {
    setAppliedFilters({ ...filters });
    setCurrentPage(1);
  };

  /**
   * CR-6448: Count from draft `filters` so badge updates immediately when
   * user selects/enters a value, matching SO Headers behavior.
   */
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== '').length,
    [filters],
  );

  const handleClearFilters = () => {
    setFilters(EMPTY_FORECAST_FILTERS);
    setAppliedFilters(EMPTY_FORECAST_FILTERS);
    // The quick box holds its own display state — clear it too, or Clear
    // Filters leaves a term visible that is no longer being applied.
    setQuickSearch('');
    if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
    setCurrentPage(1);
  };

  const handleQuickSearchChange = (value: string) => {
    setQuickSearch(value);
    setFilters((prev) => ({ ...prev, partSearch: value }));
    if (quickSearchTimerRef.current) clearTimeout(quickSearchTimerRef.current);
    quickSearchTimerRef.current = setTimeout(() => {
      setAppliedFilters((prev) => ({ ...prev, partSearch: value }));
      setCurrentPage(1);
    }, 400);
  };

  const handleRecordTypeChange = (value: string) => {
    setFilters((prev) => ({ ...prev, recordType: value }));
    setAppliedFilters((prev) => ({ ...prev, recordType: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Horizontal scroll handling
  const handleScroll = (direction: 'left' | 'right') => {
    if (tableRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left'
        ? Math.max(0, scrollPosition - scrollAmount)
        : scrollPosition + scrollAmount;
      tableRef.current.scrollLeft = newPosition;
      setScrollPosition(newPosition);
    }
  };

  // Drill-down — fetch the records behind one cell. The row's own customer
  // scopes it: sending only the (draft!) customer filter let a multi-site
  // view mix every site's records for a part into one site's modal.
  const handleCellClick = async (row: ForecastTableRowWithStock, bucketKey: string, bucketLabel: string) => {
    const qty = rowField(row, bucketKey);
    if (!qty || parseFloat(qty) === 0) return;
    if (row.record_type === 3) return; // Total rows are calculated, not drillable

    const rowSupplier = rowField(row, 'supplier') || '';
    setDrilldownTitle(`${row.part_number} — ${rowSupplier || 'Unassigned'} — ${RECORD_TYPE_LABELS[row.record_type]} — ${bucketLabel}`);
    setDrilldownOpen(true);
    setDrilldownLoading(true);
    try {
      const response = await purchasingAPI.getForecastCellDetail({
        part_number: row.part_number,
        record_type: row.record_type as 1 | 2,
        date_bucket: bucketKey,
        customer_id: rowId(row, 'customer_id')
          ?? (appliedFilters.customerId ? Number(appliedFilters.customerId) : undefined),
        supplier_id: rowId(row, 'supplier_id')
          ?? (appliedFilters.supplierId ? Number(appliedFilters.supplierId) : undefined),
        // CR-5310: the modal has no pager, so request a generous page so the
        // aggregate 'total'/'future' drill-downs list their full breakdown.
        page_size: 500,
      });
      const detail = response.data as Record<string, unknown>;
      setDrilldownData((detail['results'] || detail['rows'] || []) as Record<string, unknown>[]);
    } catch {
      toast.error('Failed to load detail data');
      setDrilldownData([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  // CR-7262: the rounding is the shared formatter's; the zero-suppression is
  // this grid's own rule — a forecast reads better with blanks than zeros.
  const formatQty = (value: string | undefined) => {
    if (!value) return '-';
    const num = parseFloat(value);
    if (!Number.isFinite(num) || num === 0) return '-';
    return formatQuantity(num);
  };

  // Customer review 11 Sep (item 35c): the Total (Open PO + Forecast)
  // row follows the customer's Admin Setup switch, read server-side; the
  // viewer's own flag only covers an older response without the field.
  const showTotalRow = gridOptions.show_total_row ?? isFeatureEnabled('show_forecast_total');

  // Group rows by (part_number, supplier) so every distinct supplier for the
  // same part renders its own contiguous block (Open PO, Forecast[, Total]).
  const sortedData = useMemo(() => {
    const groups: Map<string, ForecastTableRowWithStock[]> = new Map();
    data.forEach((row) => {
      if (!showTotalRow && row.record_type === 3) return;
      const key = `${row.part_number} ${rowField(row, 'supplier') || ''}`;
      const group = groups.get(key);
      if (group) group.push(row);
      else groups.set(key, [row]);
    });
    const result: ForecastTableRowWithStock[] = [];
    groups.forEach((group) => {
      group.sort((a, b) => RECORD_TYPE_ORDER[a.record_type] - RECORD_TYPE_ORDER[b.record_type]);
      result.push(...group);
    });
    return result;
  }, [data, showTotalRow]);

  // Columns from the buckets, trimmed to the applied forecast date window: a
  // bucket is shown when it overlaps [dateFrom, dateTo] (empty = no limit).
  // ISO dates compare as strings. Purely presentational — rows and counts are
  // untouched, so it behaves the same on the snapshot (scheduling tenants) and live paths.
  const layoutColumns = useMemo(() => {
    const { dateFrom, dateTo } = appliedFilters;
    const toColumns = (list: ForecastDateBucket[]) => list
      .filter((b) => !(dateFrom && b.end_date && b.end_date < dateFrom) && !(dateTo && b.start_date && b.start_date > dateTo))
      .map((b) => ({ key: b.key, label: b.label, tooltip: `${b.start_date} to ${b.end_date}` }));
    return {
      weekColumns: toColumns(buckets?.weeks || []),
      monthColumns: weeksOnly ? [] : toColumns(buckets?.months || []),
    };
  }, [buckets, appliedFilters, weeksOnly]);

  const layout: ForecastGridLayout = {
    showPoNumber: Boolean(gridOptions.show_po_number),
    showSupplierPartNo: !gridOptions.hide_supplier_part_no,
    showOrderRequirements: Boolean(gridOptions.show_order_requirements),
    showStockStatus,
    showFirmDays,
    weeksOnly,
    ...layoutColumns,
  };

  // Week count is install-dependent (25 on schedule-line tenants, 12
  // elsewhere): read it from the rendered columns, dropping a segment the
  // date window trims to zero. Before the first load, the install default.
  const horizonLabel = (() => {
    const loaded = (buckets?.weeks?.length || 0) > 0 || (buckets?.months?.length || 0) > 0;
    if (!loaded) return 'Weeks 1-12 | Months 1-24 | Future';
    const segs: string[] = [];
    if (layout.weekColumns.length) segs.push(`Weeks 1-${layout.weekColumns.length}`);
    if (layout.monthColumns.length) segs.push(`Months 1-${layout.monthColumns.length}`);
    if (!weeksOnly) segs.push('Future');
    return segs.join(' | ');
  })();

  if (loading && data.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading forecast data...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button onClick={fetchData} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
              Try again
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <ForecastToolbar
            partsCount={partsCount}
            refreshStatus={refreshStatus}
            refreshing={refreshing}
            onRefresh={handleRefreshSnapshot}
            exporting={exporting}
            onExport={handleExport}
            quickSearch={quickSearch}
            quickSearchPlaceholder={gridOptions.hide_supplier_part_no ? 'Search Part or PO' : 'Search Part, Supplier Part or PO'}
            onQuickSearchChange={handleQuickSearchChange}
            showFilters={showFilters}
            onToggleFilters={() => setShowFilters(!showFilters)}
            activeFilterCount={activeFilterCount}
          />
        </CardHeader>
        <CardContent>
          {showFilters && (
            <ForecastFiltersPanel
              filters={filters}
              onFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
              activeFilterCount={activeFilterCount}
              onApply={handleSearch}
              onClear={handleClearFilters}
              onHide={() => setShowFilters(false)}
              filterOptions={filterOptions}
              cascadeFilterOptions={cascadeFilterOptions}
              isCustomer={isCustomer}
              isSupplier={isSupplier}
              isScheduleTenant={isScheduleTenant}
              supplierSiteName={user?.companyName}
            />
          )}

          <ForecastGridControls
            recordType={appliedFilters.recordType}
            onRecordTypeChange={handleRecordTypeChange}
            horizonLabel={horizonLabel}
            canScrollLeft={scrollPosition !== 0}
            onScroll={handleScroll}
          />

          <ForecastGridTable
            rows={sortedData}
            layout={layout}
            tableRef={tableRef}
            onScroll={(e) => setScrollPosition(e.currentTarget.scrollLeft)}
            formatQty={formatQty}
            onCellClick={handleCellClick}
          />

          <ForecastLegend showTotalRow={showTotalRow} showStockStatus={showStockStatus} />

          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={PAGE_SIZE}
                onPageChange={handlePageChange}
                // Pages count rows (Open PO / Forecast / Total per part);
                // the title's "(N parts)" is the part count.
                itemLabel="rows"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ForecastDrilldownDialog
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
        title={drilldownTitle}
        loading={drilldownLoading}
        rows={drilldownData}
      />
    </>
  );
}
